import { Component, DoCheck, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterStateSnapshot } from '@angular/router';
import { FormBuilder, Validators } from '@angular/forms';
import { SurveyService } from '../../../services/surveys.service';
import { ToastService } from '../../../services/general/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { MatStepper } from '@angular/material/stepper';
import { UserService } from '../../../services/user.service';
import { HeaderTitleService } from '../../../services/general/header-title.service';
import { isEqual } from 'lodash-es';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
    selector: 'app-survey-response-page',
    templateUrl: './survey-response-page.component.html',
    styleUrls: ['./survey-response-page.component.scss'],
    standalone: false
})
export class SurveyResponsePageComponent implements OnInit, OnDestroy, DoCheck {
  @Input() surveyID = null;
  @Input() impersonateEmail = null;
  @Input() isAnonymous = false;
  @ViewChild('stepper', { static: false }) stepper: MatStepper;
  userInfoForm = this._formBuilder.group({
    first_name: ['', Validators.required],
    last_name: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    phone_number: ['', Validators.pattern('^[0-9]+$')],
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private _formBuilder: FormBuilder,
    private surveyService: SurveyService,
    public userService: UserService,
    private toast: ToastService,
    private translateService: TranslateService,
    private headerTitleService: HeaderTitleService
  ) {
  }

  loading = false;
  survey_id = null;
  survey = null;
  autoSaveLoading = false;

  answerStatus = null;
  answerTimestamp = null;
  justSubmitted = null;

  private surveySnapshot: any = null;
  private surveyChange$ = new Subject<void>();
  private surveyChangeSub: any;

  ngOnInit(): void {
    this.init();
  }

  init() {
    if (this.surveyID) {
      this.survey_id = this.surveyID;
      this.getSurvey();
      return;
    }
    this.route.params.subscribe(params => {
      this.survey_id = params.survey_id;
      this.getSurvey();
    });
  }


  isQuestionsValid = false;

  ngDoCheck() {
    if (this.survey && !isEqual(this.survey, this.surveySnapshot)) {
      this.surveySnapshot = JSON.parse(JSON.stringify(this.survey));
      this.checkQuestionsValidity();
      this.surveyChange$.next();
    }
  }

  checkQuestionsValidity() {
    const status = this.surveyService.surveyAnswerIsValid(this.survey, false);
    // @ts-ignore
    this.isQuestionsValid = status?.valid;
  }

  ngOnDestroy() {
    if (this.surveyChangeSub) {
      this.surveyChangeSub.unsubscribe();
    }
  }

  getSurvey() {
    this.loading = true;
    const params: any = {};
    if (this.impersonateEmail) {
      params.impersonate_email = this.impersonateEmail;
    }
    this.surveyService.surveyDetailsResponsePage(this.survey_id, params, (result) => {
      if (result?.is_draft) {
        this.router.navigate(['/']);
      }

      if (result.answered) {
        this.impersonateEmail = true;
        this.surveyID = this.survey_id;
      }

      const draft = result?.draft?.customs?.draft;
      this.answerTimestamp = result?.draft?.created_at || result?.draft?.customs?.draft?.created_at;
      this.answerStatus = result.draft?.status;
      delete result?.draft;
      this.survey = result;

      if (draft && draft.questions) {
        // 1. Creiamo una Map per le domande del draft: O(M)
        const draftQuestionsMap = new Map(draft.questions.map(dq => [dq.id, dq]));

        // 2. Iteriamo sulle domande della survey: O(N)
        this.survey.questions = this.survey.questions.map(surveyQuestion => {
          const draftQuestion = draftQuestionsMap.get(surveyQuestion.id);

          if (!draftQuestion) {
            return surveyQuestion;
          }

          // @ts-ignore
          const updatedQuestion = {
            ...surveyQuestion,
            // @ts-ignore
            response: draftQuestion.response
          };

          // 3. Ottimizzazione Checkboxes: O(C + D)
          // @ts-ignore
          if (updatedQuestion.choices && draftQuestion.choices) {
            const draftChoicesMap = new Map(
              // @ts-ignore
              draftQuestion.choices.map(dc => [String(dc.id), dc])
            );

            updatedQuestion.choices = updatedQuestion.choices.map(sc => {
              const dc = draftChoicesMap.get(String(sc.id));
              // @ts-ignore
              return dc ? { ...sc, checked: !!dc.checked } : sc;
            });
          }

          // @ts-ignore
          if (updatedQuestion.grid_rows && draftQuestion.grid_rows) {
            const draftRowsMap = new Map(
              // @ts-ignore
              draftQuestion.grid_rows.map(dr => [String(dr.id), dr])
            );

            updatedQuestion.grid_rows = updatedQuestion.grid_rows.map(sr => {
              const dr = draftRowsMap.get(String(sr.id));
              // @ts-ignore
              return dr ? { ...sr, answer_id: dr.answer_id ? String(dr.answer_id) : null } : sr;
            });
          }

          return updatedQuestion;
        });
      }

      if (this.impersonateEmail) {
        this.survey.answered = true;
      }

      if (result.visibility_type === 'PRIVATE') {
        this.userService.getUser(() => {
          this.userService.resetCurrentLanguage();
        });
      } else {
        if (!this.userService.currentOrganization) {
          this.userService.currentOrganization = this.survey.organization;
        }
      }

      if (!this.userService.userInfo) {
        this.survey.user = {
          first_name: '',
          last_name: '',
          email: '',
          phone_number: '',
          custom_fields: {}
        };

        // Add custom fields to userInfoForm
        (this.survey.team_member_custom_fields || []).forEach(field => {
          if (field.visible) {
            const validators = [];
            if (field.required) {
              validators.push(Validators.required);
            }
            (this.userInfoForm as any).addControl(field.id, this._formBuilder.control('', validators));
            this.survey.user.custom_fields[field.id] = '';
          }
        });

        if (this.survey.customs && this.survey.customs.anagraphic_is_mandatory === false) {
          this.userInfoForm.get('first_name').clearValidators();
          this.userInfoForm.get('first_name').updateValueAndValidity();
          this.userInfoForm.get('last_name').clearValidators();
          this.userInfoForm.get('last_name').updateValueAndValidity();
          this.userInfoForm.get('email').setValidators([Validators.email]);
          this.userInfoForm.get('email').updateValueAndValidity();
        }
      }
      this.headerTitleService.setTitle(result.title);
      this.survey.edit_after_submit = !!this.survey.edit_after_submit;
      this.survey.attachments = result.attachment_files?.concat(result.attachment_pubmeds);

      if (result.visibility_type === 'PRIVATE') {
        setTimeout(() => {
          this.surveyChangeSub = this.surveyChange$
            .pipe(debounceTime(5000))
            .subscribe(() => {
              if (this.survey && !this.autoSaveLoading && !this.survey.answered) {
                this.autoSave();
              }
            });
        }, 5000);
      }

      this.loading = false;
    }, (error) => {
      this.router.navigate(['/']);
      this.loading = false;
    });
  }

  surveyResponseIsValid() {
    const status = this.surveyService.surveyAnswerIsValid(this.survey, true);
    // @ts-ignore
    if (!status?.valid) {
      // @ts-ignore
      this.toast.error(this.translateService.instant('generic.warning'), this.translateService.instant(status?.message));
      return false;
    }

    if (this.survey.visibility_type === 'PUBLIC' && !this.userService.userInfo) {
      if (this.userInfoForm.invalid) {
        this.toast.error(this.translateService.instant('generic.warning'), this.translateService.instant('swal.errors.field-mandatory'));
        return false;
      }
    }

    if (this.survey.visibility_type === 'PUBLIC' && !this.userService.userInfo) {
      if (this.userInfoForm.invalid) {
        this.toast.error(this.translateService.instant('generic.warning'), this.translateService.instant('swal.errors.field-mandatory'));
        return false;
      }
    }

    return true;
  }

  onSubmit(is_draft = false, options: { skipToast?: boolean } = {}) {
    const submit = () => {
      this.loading = true;
      this.surveyService.createSurveyAnswer(this.survey_id, this.survey, (result) => {
        if (result?.customs?.draft) {
          const attachments = this.survey.attachments; // Preserve survey-level attachments
          this.survey = result.customs.draft;
          this.survey.attachments = attachments;
        }
        this.survey.pre_public_survey_answer_id = result.id;
        if (!is_draft) {
          this.stepper.next();
          this.justSubmitted = true;
          this.survey.answered = true;
        }
        if (!options.skipToast) {
          this.toast.success(
            this.translateService.instant('generic.success'),
            this.translateService.instant(is_draft ? 'pages.surveys.response.drafted-successfully' : 'pages.surveys.response.submitted-successfully'),
          );
        }
        this.loading = false;
      }, () => {
        this.loading = false;
      });
    };

    this.survey.is_draft = is_draft;
    if (is_draft) {
      submit();
    } else {
      if (this.surveyResponseIsValid()) {
        submit();
      }
    }
  }

  autoSave() {
    this.survey.is_draft = true;
    this.autoSaveLoading = true;
    this.surveyService.createSurveyAnswer(this.survey_id, this.survey, (result) => {
      this.autoSaveLoading = false;
    }, () => {
      this.autoSaveLoading = false;
    });
  }

  shouldShowAnagraphicAt(position: 'START' | 'END'): boolean {
    if (!this.survey || this.survey.visibility_type !== 'PUBLIC' || this.userService.userInfo) {
      return false;
    }
    const currentPosition = this.survey.customs?.anagraphic_position || 'START';
    return currentPosition === position;
  }

  validateQuestionsAndProceed(stepper: MatStepper) {
    const status = this.surveyService.surveyAnswerIsValid(this.survey, true);
    // @ts-ignore
    if (!status?.valid) {
      // @ts-ignore
      this.toast.error(this.translateService.instant('generic.warning'), this.translateService.instant(status?.message));
      return;
    }

    if (
      this.survey.customs?.anagraphic_position == 'END' &&
      this.survey.visibility_type == 'PUBLIC' &&
      !this.survey.customs?.anagraphic_is_mandatory
    ) {
      this.onSubmit(false, {
        skipToast: true
      });
    } else {
      stepper.next();
    }
  }

  get answeredQuestions() {
    return this.survey?.questions?.filter(q => this.surveyService.getQuestionStatus(q).status === 'answered') || [];
  }

  get partialQuestions() {
    return this.survey?.questions?.filter(q => this.surveyService.getQuestionStatus(q).status === 'partial') || [];
  }

  get missingQuestions() {
    return this.survey?.questions?.filter(q => this.surveyService.getQuestionStatus(q).status === 'missing') || [];
  }

  getQuestionCompletionSteps(q) {
    return this.surveyService.getQuestionCompletionSteps(q);
  }

  getQuestionValidationMessage(q): string {
    return this.surveyService.getQuestionStatus(q).message || '';
  }
}
